/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Brand Glossaries & Terminology Data Transfer Objects (DTOs)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/** BCP-47 style language code (e.g. "en", "vi", "fr"). */
const LanguageCodeSchema = z
  .string()
  .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'Invalid language code')
  .openapi({ description: 'BCP-47 language code', example: 'en' });

// ============================================================================
// GLOSSARY DICTIONARY SCHEMAS
// ============================================================================

export const GlossarySchema = z
  .object({
    id: z.string().openapi({ description: 'Unique glossary UUID', example: 'glo_f823a9b1' }),
    workspaceId: z.string().openapi({ description: 'Workspace owner ID', example: 'ws_default_01' }),
    name: z.string().openapi({ description: 'Glossary title', example: 'Fintech Corporate TermBase' }),
    sourceLang: LanguageCodeSchema,
    targetLang: LanguageCodeSchema,
    description: z.string().nullable().optional().openapi({ description: 'Glossary description and scope' }),
    termCount: z.number().int().nonnegative().default(0).openapi({ description: 'Total active terms contained in glossary' }),
    createdAt: z.string().datetime().openapi({ description: 'Creation ISO timestamp' }),
    updatedAt: z.string().datetime().openapi({ description: 'Last updated ISO timestamp' }),
  })
  .openapi('Glossary');

export type GlossaryDto = z.infer<typeof GlossarySchema>;

export const CreateGlossarySchema = z
  .object({
    name: z.string().min(1).max(150).openapi({ description: 'Glossary name', example: 'Fintech Corporate TermBase' }),
    sourceLang: LanguageCodeSchema,
    targetLang: LanguageCodeSchema,
    description: z.string().max(500).optional().openapi({ description: 'Optional description of terminology domain' }),
  })
  .openapi('CreateGlossary');

export type CreateGlossaryDto = z.infer<typeof CreateGlossarySchema>;

export const UpdateGlossarySchema = z
  .object({
    name: z.string().min(1).max(150).optional().openapi({ description: 'Updated glossary name' }),
    description: z.string().max(500).optional().openapi({ description: 'Updated description' }),
  })
  .openapi('UpdateGlossary');

export type UpdateGlossaryDto = z.infer<typeof UpdateGlossarySchema>;

export const GlossaryListResponseSchema = z
  .object({
    items: z.array(GlossarySchema).openapi({ description: 'List of glossaries' }),
    total: z.number().int().nonnegative().openapi({ description: 'Total count' }),
  })
  .openapi('GlossaryListResponse');

export type GlossaryListResponseDto = z.infer<typeof GlossaryListResponseSchema>;

// ============================================================================
// GLOSSARY TERM SCHEMAS
// ============================================================================

export const GlossaryTermSchema = z
  .object({
    id: z.string().openapi({ description: 'Term UUID identifier', example: 'term_91c4b2a8' }),
    glossaryId: z.string().openapi({ description: 'Parent glossary UUID', example: 'glo_f823a9b1' }),
    sourceTerm: z.string().openapi({ description: 'Source language term', example: 'Credit Ledger' }),
    targetTerm: z.string().openapi({ description: 'Approved target language translation', example: 'Sổ cái tín chỉ' }),
    caseSensitive: z.boolean().default(false).openapi({ description: 'Requires exact case matching' }),
    doNotTranslate: z.boolean().default(false).openapi({ description: 'Must remain untranslated in target output' }),
    notes: z.string().nullable().optional().openapi({ description: 'Usage context or explanatory notes' }),
    createdAt: z.string().datetime().openapi({ description: 'Creation ISO timestamp' }),
    updatedAt: z.string().datetime().openapi({ description: 'Last updated ISO timestamp' }),
  })
  .openapi('GlossaryTerm');

export type GlossaryTermDto = z.infer<typeof GlossaryTermSchema>;

export const CreateGlossaryTermSchema = z
  .object({
    sourceTerm: z.string().min(1).max(255).openapi({ description: 'Source term or compound phrase', example: 'Credit Ledger' }),
    targetTerm: z.string().min(1).max(255).openapi({ description: 'Target approved translation', example: 'Sổ cái tín chỉ' }),
    caseSensitive: z.boolean().default(false).optional().openapi({ description: 'Enforce case sensitivity' }),
    doNotTranslate: z.boolean().default(false).optional().openapi({ description: 'Keep term in original source language' }),
    notes: z.string().max(500).optional().openapi({ description: 'Contextual guidelines for translators and AI' }),
  })
  .openapi('CreateGlossaryTerm');

export type CreateGlossaryTermDto = z.infer<typeof CreateGlossaryTermSchema>;

export const UpdateGlossaryTermSchema = z
  .object({
    sourceTerm: z.string().min(1).max(255).optional(),
    targetTerm: z.string().min(1).max(255).optional(),
    caseSensitive: z.boolean().optional(),
    doNotTranslate: z.boolean().optional(),
    notes: z.string().max(500).optional(),
  })
  .openapi('UpdateGlossaryTerm');

export type UpdateGlossaryTermDto = z.infer<typeof UpdateGlossaryTermSchema>;

export const BatchCreateGlossaryTermsSchema = z
  .object({
    terms: z.array(CreateGlossaryTermSchema).min(1).max(500).openapi({ description: 'List of terms to insert in bulk' }),
  })
  .openapi('BatchCreateGlossaryTerms');

export type BatchCreateGlossaryTermsDto = z.infer<typeof BatchCreateGlossaryTermsSchema>;

export const GlossaryTermsListResponseSchema = z
  .object({
    items: z.array(GlossaryTermSchema).openapi({ description: 'List of glossary terms' }),
    total: z.number().int().nonnegative().openapi({ description: 'Total term count' }),
  })
  .openapi('GlossaryTermsListResponse');

export type GlossaryTermsListResponseDto = z.infer<typeof GlossaryTermsListResponseSchema>;
